<template>
  <FormKit
    v-if="!loyaltyLevelResponse?.skipLoyaltyReviewStep"
    :disabled="!step.isActive"
    type="form"
    :actions="false"
    :incompleteMessage="false"
    @submit="onSubmit"
  >
    <div class="mb-8">
      <div class="py-5 px-6 rounded bg-racq-white-background flex flex-wrap">
        <div class="flex">
          <div class="mr-4">
            <img :src="accountInfoBoxSettings.icon" alt="user profile icon" class="min-w-12 w-12 min-h-12 h-12" />
          </div>
          <div class="text-base mb-4 md:mb-0">
            <span
              :data-testid="loyaltyLevelResponse?.useHeading"
              class="text-dark-grey-text pr-1"
              v-html="heading"
            ></span>
            <span
              v-if="loyaltyLevelResponse"
              class="input-spacing text-base contents text-racq-med-grey"
              :data-testid="loyaltyLevelResponse?.useLoyaltyText"
              v-html="loyaltyLevelText"
            ></span>
          </div>
        </div>
      </div>

      <RichText
        v-if="loyaltyLevelResponse"
        class="text-lg pt-[24px] mb-4 text-dark-grey-text"
        :field="{ value: newQuoteCostText }"
        :data-testid="loyaltyLevelResponse?.useNewQuoteCostText"
      />

      <div class="mt-2 mb-5">
        <button type="button" class="link text-m" @click="showWhyQuoteChangedModal = true">
          {{ quoteChangedModalData?.modalTriggerLinkText?.value }}
        </button>
      </div>

      <RichText
        v-if="loyaltyLevelResponse && discountText"
        class="input-spacing text-lg"
        :field="{ value: discountText }"
        :data-testid="loyaltyLevelResponse?.discountText"
      />

      <RichText
        v-if="loyaltyLevelResponse"
        class="input-spacing text-sm text-racq-med-grey"
        :field="{ value: helpText }"
        :data-testid="loyaltyLevelResponse?.useHelpText"
      />

      <ModalDialog
        :modelValue="showWhyQuoteChangedModal"
        :showXCloseButton="true"
        :closeOnEsc="false"
        size="tooltip"
        @update:modelValue="(value) => onModalClose(value)"
      >
        <div class="sticky-modal-header">{{ quoteChangedModalData?.modalTitle?.value }}</div>
        <div class="tooltip-body richtext" v-html="quoteChangedModalData?.modalBody?.value"></div>
      </ModalDialog>
    </div>

    <FormStepFooter
      :step="step"
      :btnNextText="step.btnNext"
      :btnPrevText="step.btnPrev"
      :hideEditButton="step.hideEditButton"
    />
  </FormKit>
</template>
<script lang="ts">
const refreshOriginalQuote = 'refreshOriginalQuote';
const nextClicked = 'nextClicked';

export default {};
</script>
<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import FormStepFooter from '@/components/FormStepFooter/FormStepFooter.vue';
import { IFormStep, IQuote } from '@/interfaces';
import { useFormStore, useJeopardyStore } from '@/store';
import { replaceLoyaltyReviewBookmarks } from '@/forms/insurance/quote/home-and-contents-purchase/mappings/loyalty-review-mapping';
import useFormInputData from '@/composables/useFormInputData';
import { displayLoyaltyReview } from '@/utils/display-rules/loyalty-rules';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import ModalDialog from '../ModalDialog/ModalDialog.vue';

const formStore = useFormStore();
const jpStore = useJeopardyStore();

/*
This is a little complicated.

1. Loyalty Review relies on the comparing differences between the original quote and the "active" quote.
2. Whenever a party match occurs we don't want to show a stale message that could be inaccurate.
3. So the checkLoyaltyForSteps are the steps you may want to run the loyalty check and it will also update the original quote with the active quote.
4. This means if a loyalty has been shown and the user goes back to one the checkLoyaltyForSteps and no party match is triggered then
    it will not show it again as the original quote is in sync with active quote.
5. If going back to one of the checkLoyaltyForSteps
*/
const props = defineProps<{
  step: IFormStep;
  quote: Partial<IQuote>;
  originalQuote?: Partial<IQuote>;
  storyBookIgnoreApiMode?: boolean;
  checkLoyaltyForSteps: string[];
  loyaltyReviewStepId: string;
  hasPremiumChanged: boolean;
}>();

const loyaltyLevelResponse = ref<any>();
const heading = ref<string>('');
const loyaltyLevelText = ref<string>('');
const discountText = ref<string>('');
const newQuoteCostText = ref<string>('');
const helpText = ref<string>('');
const quoteChangedModalData = ref<any>();
const showWhyQuoteChangedModal = ref<boolean>(false);
const accountInfoBoxSettings = ref<Record<string, any>>({
  icon: require('@/assets/img/icons/user-icon.svg'),
  unauthenticatedButtonText: '',
  unauthenticatedGreetings: heading,
  params: {
    authenticated: false,
    firstName: '',
  },
});

const emit = defineEmits([refreshOriginalQuote, nextClicked]);

onMounted(async () => {
  if (!props.quote || !props.quote.quoteType) return;

  quoteChangedModalData.value = props.step.formInputs?.modalWhyHasMyQuoteChanged?.datasource?.fields;

  await checkLoyaltyReview();
  emit(refreshOriginalQuote);
});

watch(
  () => formStore.currentStep,
  async (newValue) => {
    if (props.checkLoyaltyForSteps?.find((x) => x == newValue?.id)) {
      await checkLoyaltyReview();
      emit(refreshOriginalQuote);
    }
  }
);

async function checkLoyaltyReview() {
  loyaltyLevelResponse.value = await displayLoyaltyReview(
    props.quote as IQuote,
    props.originalQuote as IQuote,
    props.storyBookIgnoreApiMode //storyBookIgnoreApiMode is used from storybook, api call fails and will not execute following otherwise.
  );

  if (loyaltyLevelResponse.value.skipLoyaltyReviewStep && props.step.stepIndex) {
    formStore.hideSpecificStep(props.loyaltyReviewStepId);
  }

  updateContent(loyaltyLevelResponse);
}

function updateContent(loyaltyLevelResponse: any) {
  const { formInputData } = useFormInputData(props.step.formInputs!);

  if (!loyaltyLevelResponse.value.skipLoyaltyReviewStep) {
    heading.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useHeading).label ?? '',
      props.quote
    );

    loyaltyLevelText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useLoyaltyText).label ?? '',
      props.quote
    );

    /*
     In sitecore 8 this is only used for Car Hire it seems. litDiscount.Text = string.Format(eligibleForFreeCarHire ? CurrentItem.GetFieldValue("RVMEntitlementWithHireCar_Text") : CurrentItem.GetFieldValue("RVMEntitlementNoHireCar_Text"), discount);
     A content field called textDiscount will need to be added to any step needing this.
    */
    discountText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.textDiscount).label ?? '',
      props.quote
    );

    newQuoteCostText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useNewQuoteCostText).label ?? '',
      props.quote
    );

    helpText.value = replaceLoyaltyReviewBookmarks(
      formInputData(loyaltyLevelResponse.value.useHelpText).label ?? '',
      props.quote
    );
  }
}

async function onSubmit() {
  try {
    formStore.showLoadingForCurrentStep();
    formStore.showLoadingForCurrentStep(false);

    emit(nextClicked);

    formStore.goNextStep();
  } catch (jPCode: any) {
    formStore.showLoadingForCurrentStep(false);
    showJeopardyPanel(jPCode);
  }
}

function showJeopardyPanel(jpCode: string) {
  jpStore.showJeopardyPanel({
    jpCode: jpCode,
  });
}

function onModalClose(e: any) {
  showWhyQuoteChangedModal.value = e;
}
</script>
